/** Vocabulary review — spaced repetition over the Wortschatzbank.
 *
 * The card shows the German side first and the learner self-marks. That is
 * deliberate: recall ("what does this mean?") is what the schedule is
 * measuring, and a multiple-choice guess would inflate the box count
 * without the learner actually knowing the word.
 *
 * Answers post one at a time rather than batching at the end, so a learner
 * who closes the tab halfway keeps the progress they earned.
 */
import { useCallback, useEffect, useState } from "react";
import * as api from "../../api/reference";
import type { ReviewCard, ReviewStats } from "../../api/reference";
import { Button, Card, Spinner } from "../../components/ui";
import { TapToTranslate } from "../../components/TapToTranslate";
import { useT } from "../../i18n";
import { speak } from "../../utils/tts";

export function VocabReview({ level }: { level: string }) {
  const t = useT();
  const [queue, setQueue] = useState<ReviewCard[] | null>(null);
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setQueue(null);
    setIndex(0);
    setRevealed(false);
    api.reviewQueue(level, 20).then(setQueue).catch(() => setQueue([]));
    api.reviewStats(level).then(setStats).catch(() => setStats(null));
  }, [level]);

  useEffect(load, [load]);

  if (!queue) return <Spinner />;

  const card = queue[index];
  const done = index >= queue.length;

  const answer = async (correct: boolean) => {
    if (!card || busy) return;
    setBusy(true);
    try {
      await api.answerCard(card.entry_id, correct);
    } finally {
      setBusy(false);
      setRevealed(false);
      setIndex((i) => i + 1);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {stats && (
        <div className="flex flex-wrap gap-4 text-sm text-muted">
          <span>{t("review.due", { count: stats.due_today })}</span>
          <span>{t("review.learned", { count: stats.learned, total: stats.total })}</span>
        </div>
      )}

      {done ? (
        <Card>
          <p className="text-lg font-bold text-ink">{t("review.sessionDone")}</p>
          <p className="mt-1 text-sm text-muted">
            {t("review.reviewed", { count: queue.length })}
          </p>
          <Button onClick={load} className="mt-3">
            {t("review.again")}
          </Button>
        </Card>
      ) : (
        <Card>
          <div className="flex items-center justify-between text-xs text-muted">
            <span>{t("review.position", { n: index + 1, total: queue.length })}</span>
            <span>
              {card.is_new ? t("review.newWord") : t("review.box", { box: card.box })}
            </span>
          </div>

          <p className="mt-4 text-center text-3xl font-extrabold text-ink">
            {card.article ? `${card.article} ` : ""}
            {card.de}
          </p>

          <div className="mt-3 flex justify-center">
            <Button variant="secondary" onClick={() => speak(card.de)}>
              🔊 {t("player.listen")}
            </Button>
          </div>

          {!revealed ? (
            <Button onClick={() => setRevealed(true)} className="mt-6 w-full">
              {t("review.reveal")}
            </Button>
          ) : (
            <div className="mt-6">
              <p className="text-center text-xl text-ink">{card.en}</p>
              {card.plural && card.plural !== "-" && (
                <p className="mt-1 text-center text-sm text-muted">
                  {t("review.plural")}: {card.plural}
                </p>
              )}
              {/* Shown only after reveal, so translating it can't leak the
                  answer the recall test is checking. */}
              {card.example && (
                <div className="mt-3 text-center text-sm italic text-muted">
                  <TapToTranslate text={card.example} />
                </div>
              )}
              <div className="mt-6 flex gap-3">
                <Button
                  variant="secondary"
                  className="flex-1"
                  disabled={busy}
                  onClick={() => answer(false)}
                >
                  {t("review.forgot")}
                </Button>
                <Button className="flex-1" disabled={busy} onClick={() => answer(true)}>
                  {t("review.knew")}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
